// Fork: /request-account/ (templates/zerver/request_account.html). Switches
// between a new company address and the person's own email, suggests the
// company address from the typed name and checks it is still free; the
// password strength bar and show/hide toggle come from signup.ts.
import {$} from "jquery";
import * as z from "zod/mini";

import "../../styles/portico/request_account.css";
import {$t} from "../i18n.ts";

const check_response_schema = z.object({
    msg: z.optional(z.string()),
    suggestion: z.optional(z.string()),
    error: z.optional(z.nullable(z.string())),
});

$(() => {
    const $form = $("#request-account-form");
    if ($form.length === 0) {
        return;
    }
    const $first = $<HTMLInputElement>("input#id_first_name");
    const $last = $<HTMLInputElement>("input#id_last_name");
    const $local = $<HTMLInputElement>("input#id_local_part");
    const $hint = $("#local-part-hint");
    const $password = $<HTMLInputElement>("input#id_password");
    const $password2 = $<HTMLInputElement>("input#id_password2");
    // A value already there (re-rendered after a server error) counts as typed by the user.
    let edited = $local.val() !== "";
    let timer: ReturnType<typeof setTimeout> | undefined;

    function is_personal(): boolean {
        return $form.find("input[name='kind']:checked").val() === "personal";
    }

    // Hidden + disabled: the hidden side's required fields don't block submit.
    function show_kind(): void {
        const personal = is_personal();
        $("#personal-fields").prop({hidden: !personal, disabled: !personal});
        $("#company-fields").prop({hidden: personal, disabled: personal});
        if (personal) {
            $("#id_personal_email").trigger("focus");
        } else if ($first.val() && $last.val()) {
            void check();
        }
    }
    $form.find("input[name='kind']").on("change", show_kind);

    function show_hint(text: string, is_error: boolean): void {
        $hint.text(text).toggleClass("text-error", is_error);
    }

    async function check(): Promise<void> {
        const params = new URLSearchParams({
            first_name: $first.val()!,
            last_name: $last.val()!,
            local_part: edited ? $local.val()! : "",
        });
        const response = await fetch(`/request-account/check/?${params.toString()}`);
        const data = check_response_schema.parse(await response.json());
        if (!response.ok) {
            show_hint(data.msg ?? $t({defaultMessage: "Could not check this address."}), true);
            return;
        }
        if (!edited) {
            $local.val(data.suggestion ?? "");
        }
        if (data.error) {
            show_hint(data.error, true);
        } else if ($local.val() === "") {
            show_hint($t({defaultMessage: "Suggested from your name. You can change it."}), false);
        } else {
            show_hint($t({defaultMessage: "Available"}), false);
        }
    }

    function schedule_check(): void {
        clearTimeout(timer);
        if (is_personal()) {
            // The company-address check needs cPanel; nothing to check here.
            return;
        }
        timer = setTimeout(() => void check(), 300);
    }

    $first.on("input", schedule_check);
    $last.on("input", schedule_check);
    $local.on("input", () => {
        edited = $local.val() !== "";
        schedule_check();
    });

    function passwords_match(): boolean {
        const mismatch = $password2.val() !== "" && $password.val() !== $password2.val();
        $("#password2-hint").text(mismatch ? $t({defaultMessage: "The passwords don't match."}) : "");
        return !mismatch;
    }
    $password.on("input", passwords_match);
    $password2.on("input", passwords_match);
    $form.on("submit", (e) => {
        if (!is_personal() && !passwords_match()) {
            e.preventDefault();
        }
    });

    if (!is_personal() && $first.val() && $last.val()) {
        void check();
    }
});
